import type { CSSProperties } from 'react';

export type HudVariant = 'normal' | 'alert' | 'urgent' | 'success';
export type HudFrameTone = 'cyan' | 'blue' | 'red' | 'amber' | 'violet' | 'neutral';

export type HudColorMode = 'default' | 'deuteranopia' | 'protanopia' | 'high-contrast';

export const HUD_DESIGN = {
  // Frosted glass base
  glassBg: 'rgba(8, 14, 26, 0.72)',
  glassBlur: 'blur(8px)',
  glassBorder: '1px solid rgba(60, 120, 200, 0.18)',
  borderRadius: 6,

  // Typography
  fontFamily: '"Share Tech Mono", "Courier New", monospace',
  labelColor: '#6688aa',
  valueColor: '#ddeeff',
  accentColor: '#44aaff',

  // Neon palette
  neonBlue: '#00aaff',
  neonGreen: '#22ee66',
  neonOrange: '#ff8833',
  neonRed: '#ff2244',
  neonPurple: '#aa44ff',
} as const;

export const HUD_FRAME_TONES: Record<HudFrameTone, string> = {
  cyan: '#62ddff',
  blue: '#86a9ff',
  red: '#ff6f6f',
  amber: '#ffcc66',
  violet: '#b58cff',
  neutral: '#9fb4c7',
} as const;

// Phase 65 — T2 faction palette tokens (Diamond Sword / Blood Eagle / Starwolf / Phoenix)
export const FACTION_COLORS = {
  diamondSword: '#2dd4d4', // Blue team — cyan/teal
  bloodEagle: '#ff3838',   // Red team — red/gold
  starwolf: '#a855ff',     // purple
  phoenix: '#ff8a1a',      // orange
} as const;

export function resolveHudFrameToneColor(tone: HudFrameTone = 'neutral') {
  return HUD_FRAME_TONES[tone];
}

export function resolveTeamFactionColor(team: string | number | null | undefined): string {
  const key = (team ?? '').toString().toLowerCase();
  if (key === 'blue' || key === 'diamond' || key === 'ds' || key === '0') return FACTION_COLORS.diamondSword;
  if (key === 'red' || key === 'blood' || key === 'be' || key === '1') return FACTION_COLORS.bloodEagle;
  if (key === 'purple' || key === 'starwolf' || key === '2') return FACTION_COLORS.starwolf;
  if (key === 'orange' || key === 'phoenix' || key === '3') return FACTION_COLORS.phoenix;
  return FACTION_COLORS.diamondSword;
}

export function resolveTeamAccentColor(team: number | string | null | undefined): string {
  // Phase 65 — prefer T2 faction tints for Blue/Red so HUD reflects team palette
  const key = (team ?? '').toString().toLowerCase();
  if (key === 'blue' || key === '0' || key === 'diamond') return FACTION_COLORS.diamondSword;
  if (key === 'red' || key === '1' || key === 'blood') return FACTION_COLORS.bloodEagle;
  const index = typeof team === 'number' ? team : typeof team === 'string' ? parseInt(team, 10) || 0 : 0;
  const colors = [FACTION_COLORS.diamondSword, FACTION_COLORS.bloodEagle, FACTION_COLORS.starwolf, FACTION_COLORS.phoenix, '#22ee66'];
  return colors[Math.abs(index) % colors.length];
}

export function resolveHudColorModeTokens(mode: HudColorMode = 'default') {
  if (mode === 'deuteranopia') {
    return { blue: '#48c7ff', red: '#ffb84d', accent: '#48c7ff', danger: '#ffb84d', support: '#d7f2ff' };
  }

  if (mode === 'protanopia') {
    return { blue: '#46d9ff', red: '#ffd05a', accent: '#46d9ff', danger: '#ffd05a', support: '#b8f7e5' };
  }

  if (mode === 'high-contrast') {
    return { blue: '#00e5ff', red: '#ffdd00', accent: '#ffffff', danger: '#ffdd00', support: '#ffffff' };
  }

  return { blue: '#2dd4d4', red: '#ff3838', accent: '#2dd4d4', danger: '#ff3838', support: '#8ef0c9' };
}

export function resolveHudPanelStyle(variant: HudVariant = 'normal'): CSSProperties {
  const tints: Record<HudVariant, string> = {
    normal: 'rgba(8, 14, 26, 0.72)',
    alert: 'rgba(80, 8, 8, 0.78)',  // #ff red-tinted alert
    urgent: 'rgba(60, 40, 4, 0.78)',
    success: 'rgba(4, 40, 20, 0.78)',
  };
  return {
    background: tints[variant],
    backdropFilter: HUD_DESIGN.glassBlur,
    border: HUD_DESIGN.glassBorder,
    borderRadius: HUD_DESIGN.borderRadius,
  };
}

export function resolveHealthBarColor(health: number, maxHealth: number): string {
  const safeMaxHealth = Math.max(1, maxHealth);
  const ratio = Math.max(0, Math.min(1, health / safeMaxHealth));
  if (ratio >= 0.6) return HUD_DESIGN.neonGreen;
  if (ratio >= 0.3) return HUD_DESIGN.neonOrange;
  return HUD_DESIGN.neonRed;
}

export function resolveEnergyBarColor(energy: number, maxEnergy: number): string {
  const safeMaxEnergy = Math.max(1, maxEnergy);
  const ratio = Math.max(0, Math.min(1, energy / safeMaxEnergy));
  if (ratio >= 0.5) return HUD_DESIGN.neonBlue;
  if (ratio >= 0.2) return '#8844ff';
  return HUD_DESIGN.neonPurple;
}

export function resolveFpsDisplayColor(fps: number): string {
  if (fps >= 55) return '#44ff88';
  if (fps >= 30) return '#ffcc44';
  return '#ff4444';
}

export function resolveSpeedReadoutColor(speed: number): string {
  if (speed >= 80) return HUD_DESIGN.neonGreen;
  if (speed >= 40) return HUD_DESIGN.accentColor;
  return HUD_DESIGN.labelColor;
}

export function resolveKillFeedWeaponBadge(weaponId: string): { label: string; color: string } {
  const badges: Record<string, { label: string; color: string }> = {
    spinfusor: { label: 'DISC', color: '#44aaff' },
    chaingun: { label: 'CG', color: '#ffcc44' },
    mortar: { label: 'MORT', color: '#ff8833' },
    blaster: { label: 'BLST', color: '#ffffff' },
  };
  return badges[weaponId] ?? { label: '??', color: '#8899aa' };
}
